import _ from 'lodash';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col, Panel, OverlayTrigger, DropdownButton, Tooltip, MenuItem } from 'react-bootstrap';
import { withFormik } from 'formik';

import Button from './common/Button';
import FormField from './common/FormField';
import TextArea from './common/TextArea';
import SendEmailForm from './common/SendEmailForm';
import SendSmsForm from './common/SendSmsForm';
import ConfirmDialog from './common/ConfirmDialog';
import CashInput from './common/CashInput';
import NumberInput from './common/NumberInput';
import Loading from './common/Loading';
import PaymentProvider from './common/PaymentProvider';
import LoadInvoiceItemsDialog, { getInvoiceItems } from './common/LoadInvoiceItemsDialog';
import InvoiceItems from './common/InvoiceItems';

import { isEmptyObject } from '../validators/formValidators';
import { validatePaymentForm } from '../validators/paymentFormValidator';
import { caseSetServerError,
         casePaymentGenerateAction,
         caseGetChargesAction,
         caseSetCurrentAction, 
         caseGetEvents} from '../actions/caseActions';

import { matterPartialFormSavePromise,
         getPublicPaymentUrl} from '../helpers/utils';
import { caseHashDeleteAction } from './../actions/caseActions';
import { userLawPayListAccountsAction } from '../actions/userActions';
import { getPaymentAccount, filterTasks } from './../helpers/utils';
import { getCountry } from '../helpers/internationalization';

         
// PaymentAgreementHash is not visible, however during save the whole payment json field get owerwritten,
// and the hash needs to be part of that.
export const fields = ['HourlyRate', 'BillableMinuteIncrements', 'Fee', 'FeeOwed', 'FeePaid', 'FeeUnpaid', 'MinutesTotal', 'PaymentLink'];

export const DEFAULT_BILLABLE_MINUTE_INCREMENTS = 6;

class PaymentForm extends PureComponent {
    constructor(props) {
        super(props);
        this.saveImmediate = this.saveImmediate.bind(this);
        this.requestPayment = this.requestPayment.bind(this);
        this.renderSettingsDialog = this.renderSettingsDialog.bind(this);
        this.invoiceItemsLoaded = this.invoiceItemsLoaded.bind(this);
        this.state = {
            loading: false,
            loadingUploadedFiles: false,
            loadingCharges: false,
            paymentDialogOpen: false,
            requestingPaymentUrl: false,
            paymentUrl: false,
            paymentHash: undefined,
            lawPayAccountsLoading: false,
            lawPayAccounts: undefined,
            selectedLawPayAccount: undefined,
            selectedInvoiceItemIds: undefined,
            invoiceItems: undefined,
            invoiceItemsDialogOpen: false,
      };
    }

    componentDidMount() {
        if (this.props.account && !this.state.selectedLawPayAccount && this.props.accountType === 'LawPay') {
            this.setState({selectedLawPayAccount: this.props.account});
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.currentCase) {
            if (nextProps.currentCase.id &&
                !nextProps.currentCase.charges &&
                !this.state.loadingCharges) {

                this.setState({loadingCharges: true});
                this.props.dispatch(caseGetChargesAction(nextProps.currentCase.id))
                    .then(result => {
                        this.setState({loadingCharges: false});
                    }).catch(error => {
                        this.setState({loadingCharges: false});
                    });
            }
        }

        if (nextProps.account && !this.state.selectedLawPayAccount && this.props.accountType === 'LawPay') {
            this.setState({selectedLawPayAccount: nextProps.account});
        }

        if (!isEmptyObject(this.state.selectedInvoiceItemIds)) {
            this.invoiceItemsLoaded(this.state.selectedInvoiceItemIds, nextProps);
        }
    }


    requestPayment(stateSuccess, stateFailure) {
        this.setState({requestingPaymentUrl: true});
        let account = this.props.account;
        if (this.props.accountType === 'LawPay') {
            account = this.state.selectedLawPayAccount;
        }
        this.props.dispatch(casePaymentGenerateAction({
            caseId: this.props.currentCase.id,
            description: this.props.values.Description,
            account,
            accountType: this.props.accountType,
            invoiceItems: this.state.invoiceItems,
            chargeAmount: this.props.values.ChargeAmount * 100.0
        })).then(result => {
                if (!result || !result.data || !result.data.hash) {
                    this.props.dispatch(caseSetServerError('Payment generation failure.'));
                    this.setState({ requestingPaymentUrl: false, ...stateFailure });
                    return;
                }

                const hash = result.data;
                this.setState({
                    paymentUrl: { 'payment.PaymentLink': getPublicPaymentUrl(hash) },
                    paymentHash: hash.hash,
                    ...stateSuccess
                });
            })
            .catch(error => {
                console.error(new Error().stack, error);
                this.setState({ requestingPaymentUrl: false, ...stateFailure });
                this.props.dispatch(caseSetServerError(error));
            });
    }

    async saveImmediate() {
        const values = {};
        for (let i = 0; i < fields.length; i++) {
            values[fields[i]] = this.props.values[fields[i]];
        }
        if (!isEmptyObject(validatePaymentForm(values, this.props))) {
            return;
        }

        const id = parseInt(this.props.currentCase.id);
        const matter = { payment: values };
        let savedMatter = await matterPartialFormSavePromise(this.props.dispatch, id, matter, false);
        savedMatter = savedMatter.data.currentCase;
        this.props.dispatch(caseSetCurrentAction(savedMatter));
    }

    renderSettingsDialog() {
        const {
            submitting,
            loadingCurrent,
            handleBlur,
            handleChange,
            values = {},
            touched,
            errors,
            dialogCancel,
            dialogOpen,
            resetForm,
        } = this.props;

        let loading = this.state.loading || loadingCurrent || this.state.loadingUploadedFiles;
        return (
            <ConfirmDialog title='Edit Case Payment Settings'
                           cancel={() => {
                               resetForm();
                               dialogCancel();
                           }}
                           open={dialogOpen}
                           confirm={async () => {
                               await this.saveImmediate();
                               dialogCancel();                               
                           }}
            >
            <Row>
            <Col xs={12}>
                <form>
                    <Row>
                        <Col xs={12}>
                            <CashInput field={{
                                        name: 'Fee',
                                        onChange: handleChange,
                                        onBlur: handleBlur,
                                        error: errors['Fee'],
                                        touched: touched['Fee'],
                                        value: values['Fee'],
                                    }}
                                    label={'Flat Fee'}
                                    disabled={submitting || loading}
                                    loading={loading}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <CashInput field={{
                                            name: 'HourlyRate',
                                            onChange: handleChange,
                                            onBlur: handleBlur,
                                            error: errors['HourlyRate'],
                                            touched: touched['HourlyRate'],
                                            value: values['HourlyRate'],
                                        }}
                                        label='Default Hourly Rate'
                                        disabled={submitting || loading}
                                        allowFloat={false}
                                        loading={loading}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <NumberInput field={{
                                            name: 'BillableMinuteIncrements',
                                            onChange: handleChange,
                                            onBlur: handleBlur,
                                            error: errors['BillableMinuteIncrements'],
                                            touched: touched['BillableMinuteIncrements'],
                                            value: values['BillableMinuteIncrements'],
                                        }}
                                        label='Billable Minutes Increment'
                                        defaultValue={DEFAULT_BILLABLE_MINUTE_INCREMENTS}
                                        disabled={submitting || loading}
                                        loading={loading}
                            />
                        </Col>
                    </Row>
                    </form>
                </Col>
                </Row>
            </ConfirmDialog>
        );
    }

    invoiceItemsLoaded(selectedInvoiceItemIds, props = this.props) {
        if (Object.keys(selectedInvoiceItemIds).length == 0) {
            this.setState({invoiceItems: {}, invoiceItemsDialogOpen: false, selectedInvoiceItemIds: {}});
            return;
        }

        // Get the events in sorted order
        const events = getInvoiceItems(props.currentCase, props.loggedIn, props.employees).filter(event => selectedInvoiceItemIds[event.id]);
        const items = [];
        let timeChargeAmount = 0;
        let costChargeAmount = 0;
        let costQtyTotal = 0;
        let timeTotal = 0;
        const chargeItemsIndex = [];
        for (let i = 0; i < events.length; i++) {
            const event = events[i];
            if (event['EventType'] == 'Time') {
                if (event['$InvoiceId$']) {
                    continue;
                }
                timeChargeAmount += event.amount || 0;
                timeTotal += event.time || 0;
                event['$InvoiceId$'] = true;
            } else if (event['EventType'] == 'Cost') {
                if (event['$InvoiceId$']) {
                    continue;
                }
                event['$InvoiceId$'] = true;
                for (let j = 0; j < event['items'].length; j++) {
                    const qty = event['items'][j].qty || 1;
                    costChargeAmount += event['items'][j].amount * 100 * qty;
                    costQtyTotal += qty;
                }
            } else if (event['EventType'] == 'Charge') {
                if (event['$AmountRemaining$'] == 0 || event['amount'] <= 0) {
                    continue;
                }
                chargeItemsIndex.push(items.length);
            } else {
                continue;
            }
            items.push(event);
        }

        const totalChargeAmount = timeChargeAmount + costChargeAmount;
        let chargeAmount = totalChargeAmount;
        let balanceChargeAmount = 0;
        for (let i = 0; i < chargeItemsIndex.length; i++) {
            const event = events[chargeItemsIndex[i]];
            let amountRemaining = event['amount'];
            if (event['$AmountRemaining$'] !== undefined) {
                amountRemaining = parseInt(event['$AmountRemaining$']);
            }

            if (amountRemaining == 0) {
                // This should never happen.
                console.error('Should not be 0, above check failure.');
                continue;
            }

            const remaining = Math.min(chargeAmount, amountRemaining);
            balanceChargeAmount += Math.max(amountRemaining - remaining, 0);
            chargeAmount -= remaining;
            event['$AmountInvoice$'] = remaining;
            event['$AmountRemaining$'] = Math.max(amountRemaining - remaining, 0);
            if (event['$InvoiceId$'] == undefined) {
                event['$InvoiceId$'] = {};
            }

            event['$InvoiceId$'][-1] = remaining;
        }

        const timeBillingIncrement = props.currentCase.payment['BillableMinuteIncrements'] || DEFAULT_BILLABLE_MINUTE_INCREMENTS;
        const invoiceItems = {
            id: -1,
            meta: {
                timeBillingIncrement,
                timeTotal,
                costQtyTotal,
                timeChargeAmount,
                costChargeAmount,
                totalChargeAmount,
                balanceChargeAmount,
                chargeAmount
            },
            items
        };

        if (invoiceItems.items.length > 0) console.log(invoiceItems.items[0].version)

        if (_.isEqual(this.state.invoiceItems, invoiceItems)) {
            return;
        }

        selectedInvoiceItemIds = {};
        for (let i = 0; i < invoiceItems.items.length; i++) {
            const item = invoiceItems.items[i];
            selectedInvoiceItemIds[item.id] = item;
        }

        props.setFieldValue('ChargeAmount', chargeAmount / 100.0);
        this.setState({invoiceItems, invoiceItemsDialogOpen: false, selectedInvoiceItemIds});
    }

    render() {
        const {
            submitting,
            loadingCurrent,
            handleBlur,
            handleChange,
            values = {},
            touched,
            errors,
            currentConfig,
            showAsSettingDialog = false,
            account,
            accountType,
            currentCase,
        } = this.props;

        if (showAsSettingDialog) {
            return this.renderSettingsDialog();
        }

        let loading = this.state.loading || loadingCurrent || this.state.loadingUploadedFiles;
        let buttonRow;
        let header = 'Request Payment';
        if (accountType != 'None') {
            const style = { width: 100, marginTop: '15px', float: 'left' };
            const disabled = submitting;
            buttonRow = (
                <div>
                <Button icon="credit-card"
                        style={{ ...style, float: 'right', width: 110 }}
                        label="Manual ..."
                        loadingLabel="Manual"
                        tooltip="Enter credit card information manually"
                        disabled={disabled || !values['ChargeAmount'] || values['ChargeAmount'] <= 0}
                        onClick={() => this.setState({ paymentDialogOpen: true })}/>
                <Button bsStyle="primary"
                        style={{ ...style, marginRight: 15 }}
                        label="Email ..."
                        icon="envelope"
                        loading={this.state.emailPaymentRequestLoading}
                        onClick={() => { 
                            this.requestPayment({ emailPaymentRequestDialogOpen: true, emailPaymentRequestLoading: false },
                                                { emailPaymentRequestDialogOpen: false, emailPaymentRequestLoading: false });
                        }}
                        disabled={disabled || this.state.emailPaymentRequestDialogOpen}
                        tooltip="Create a payment portal and notify client by email" />
                <Button bsStyle="primary"
                        style={style}
                        label="SMS ..."
                        icon="phone"
                        onClick={() => { 
                            this.requestPayment({ smsPaymentRequestDialogOpen: true, smsPaymentRequestLoading: false },
                                                { smsPaymentRequestDialogOpen: false, smsPaymentRequestLoading: false });
                        }}
                        disabled={disabled || this.state.smsPaymentRequestDialogOpen}
                        loading={this.state.smsPaymentRequestLoading}
                        tooltip="Create a payment portal and notify client by sms" />
                </div>
            );

            if (accountType === 'LawPay') {
                const lawPayAccount = this.state.selectedLawPayAccount;
                header = (
                    <div style={{ height: 25 }}>
                    <h6 style={{ float: 'left', fontSize: 13, marginTop: 5 }}>Request Payment</h6>
                    <div style={{ float: 'right' }}>
                    <OverlayTrigger
                        placement="left"
                        trigger={['hover', 'focus']}
                        overlay={<Tooltip id="tooltipCaseState">Choose account where the payment will go.</Tooltip>}
                    >
                        <DropdownButton
                            title={lawPayAccount ? lawPayAccount['name'] : 'Loading...'}
                            disabled={loading}
                            id="bg-nested-dropdown"
                            style={{backgroundColor: 'rgba(0,0,0,0)', marginTop: -6, marginRight: 15}}
                            onClick={async() => {
                                if (!this.state.lawPayAccounts && !this.state.lawPayAccountsLoading) {
                                    try {
                                        this.setState({lawPayAccountsLoading: true});
                                        const accounts = await userLawPayListAccountsAction().promise;
                                        const lawPayAccounts = accounts.data || [];
                                        this.setState({lawPayAccounts, lawPayAccountsLoading: false});
                                    } catch (error) {
                                        let text = 'Unable to list LawPay accounts!';
                                        console.error(text);
                                        this.props.dispatch(caseSetServerError(text));
                                        this.setState({lawPayAccountsLoading: false});
                                    }
                                }
                            }}
                        >
                        {!this.state.lawPayAccounts && <MenuItem eventKey="loadingLawPayAccounts"><Loading inline /></MenuItem>}
                        {this.state.lawPayAccounts && this.state.lawPayAccounts.filter(account => lawPayAccount && account['id'] !== lawPayAccount['id'])
                                                                            .map((lawPayAccount, index) =>
                            (<MenuItem
                                eventKey={`selectLawPayAccount${index}`}
                                onClick={() => this.setState({selectedLawPayAccount: lawPayAccount})}
                            >
                            {lawPayAccount['name']}
                            </MenuItem>))
                        }
                        </DropdownButton>
                    </OverlayTrigger>
                    </div>
                    </div>);
            }

        } else {
            buttonRow = (
                <PaymentProvider
                    renderIntegrateButtonOnly
                    loading={submitting || loading}
                    account={this.state.selectedLawPayAccount || account}
                    accountType={accountType}
                    country={getCountry(this.props.loggedIn)}
                    loggedIn={this.props.loggedIn}
                />
            );
        }

        const editEmailTemplates = (currentConfig && currentConfig["payEmailTemplates"]) || [];
        const editSmsTemplates = (currentConfig && currentConfig["paySmsTemplates"]) || [];
        let invoiceItems;
        let loadEventsAddon = <React.Fragment />;
        if (currentCase && currentCase.events && currentCase.events.filter(e => e['EventType'] == 'Time' || e['EventType'] == 'Cost').length > 0) {
            loadEventsAddon = (
                <Button
                    label="Invoice Items ..."
                    tooltip="Attach time events to the final invoice"
                    icon="time"
                    style={{marginTop: -8, marginRight: -15}}
                    onClick={() => {
                        this.setState({invoiceItemsDialogOpen: true});
                    }}
                />
            );
        }

        if (this.state.invoiceItems) {
            invoiceItems = this.state.invoiceItems;
        }

        if (invoiceItems && invoiceItems.items && invoiceItems.items.length > 0) {
            loadEventsAddon = (
                <React.Fragment>
                {loadEventsAddon}
                <Button
                    label="Clear Items"
                    tooltip="Clear invoice items ..."
                    icon="trash"
                    style={{marginTop: -8, marginRight: -15, marginLeft: 30}}
                    onClick={() => this.invoiceItemsLoaded({})}
                />
                </React.Fragment>
            );
        }

        return (
            <div>
            <Row>
                <Col xs={12}>
                <form>
                    <Panel data-hj-whitelist header={header}>
                    <Row>
                        <Col xs={12}>
                            <CashInput 
                                field={{
                                    name: 'ChargeAmount',
                                    onChange: handleChange,
                                    onBlur: handleBlur,
                                    error: errors['ChargeAmount'],
                                    touched: touched['ChargeAmount'],
                                    value: values['ChargeAmount'],
                                }}
                                label='Amount To Charge'
                                disabled={submitting || loading}
                                afterAddon={loadEventsAddon}
                            />
                        </Col>
                    </Row>
                    {invoiceItems && (
                    <Row>
                    <Col xs={12} style={{paddingLeft: 30, paddingRight: 30}}>
                        <InvoiceItems invoiceItems={invoiceItems} />
                    </Col>
                    </Row>)}
                    <Row>
                        <Col xs={12}>
                            <TextArea field={{
                                            name: 'Description',
                                            onChange: handleChange,
                                            onBlur: handleBlur,
                                            error: errors['Description'],
                                            touched: touched['Description'],
                                            value: values['Description'],
                                        }}
                                        label='Public Description'
                                        disabled={submitting || loading}
                            />
                        </Col>
                    </Row>
                    <Row>
                    <Col xs={12}>
                        <FormField label='Create Portal and Send Link'>
                        {buttonRow}
                        </FormField>
                    </Col>
                    </Row>
                    </Panel>
                    </form>
                </Col>
                <PaymentProvider
                    account={this.state.selectedLawPayAccount || account}
                    accountType={accountType}
                    showAsDialog
                    dialogOpen={this.state.paymentDialogOpen}
                    dialogCancel={() => this.setState({ paymentDialogOpen : false })}
                    loggedIn={this.props.loggedIn}
                    email={this.props.currentCase.client ? this.props.currentCase.client['Email'] : ''}
                    onError={(error) => this.props.dispatch(caseSetServerError(error))}
                    onSuccess={() => {
                        this.props.dispatch(caseGetChargesAction(this.props.currentCase.id));
                        this.setState({ paymentDialogOpen : false });
                    }}
                    amount={values['ChargeAmount'] * 100}
                    description={values['Description']}
                    caseId={this.props.currentCase.id}
                    country={getCountry(this.props.loggedIn)}
                />
                <LoadInvoiceItemsDialog
                    selectedIds={this.state.selectedInvoiceItemIds}
                    dialogOpen={this.state.invoiceItemsDialogOpen}
                    dialogCancel={() => {
                        this.setState({ invoiceItemsDialogOpen : false });
                    }}
                    dialogConfirm={ids => {
                        this.invoiceItemsLoaded(ids);
                        this.setState({ invoiceItemsDialogOpen : false });
                    }}
                />
                <SendEmailForm 
                    showAsDialog
                    initialTemplate={editEmailTemplates && {...editEmailTemplates[0]}}
                    templatesFieldName="payEmailTemplates"
                    renderTemplateLoad={false}
                    renderEmailSection
                    renderEmailButton
                    disableCaseFilesAutoload
                    renderAttachmentField={false}
                    onEmailSent={() => {
                        this.setState({ emailPaymentRequestDialogOpen: false, invoiceItems: {}, selectedInvoiceItemIds: {}});
                        this.props.setFieldValue('ChargeAmount', '');
                        this.props.setFieldValue('Description', '');
                        this.props.dispatch(caseGetEvents(this.props.currentCase.id));
                    }}
                    cancel={async () => {
                        if (this.state.paymentHash) {
                            await this.props.dispatch(caseHashDeleteAction(this.state.paymentHash));
                            this.props.dispatch(caseGetEvents(this.props.currentCase.id));
                        }

                        this.setState({ emailPaymentRequestDialogOpen: false, paymentHash: undefined })
                    }}
                    open={this.state.emailPaymentRequestDialogOpen}
                    title="Email Request for Payment"
                    extraPlaceholders={this.state.paymentUrl}
                />
                <SendSmsForm 
                    showAsDialog
                    templates={editSmsTemplates}
                    initialTemplate={editSmsTemplates && {...editSmsTemplates[0]}}
                    templatesFieldName="paySmsTemplates"
                    renderTemplateLoad={false}
                    onSmsSent={() => {
                        this.setState({ smsPaymentRequestDialogOpen: false, invoiceItems: {}, selectedInvoiceItemIds: {} });
                        this.props.setFieldValue('ChargeAmount', '');
                        this.props.setFieldValue('Description', '');
                        this.props.dispatch(caseGetEvents(this.props.currentCase.id));
                    }}
                    cancel={async () => {
                        if (this.state.paymentHash) {
                            await this.props.dispatch(caseHashDeleteAction(this.state.paymentHash));
                            this.props.dispatch(caseGetEvents(this.props.currentCase.id));
                        }

                        this.setState({ smsPaymentRequestDialogOpen: false, paymentHash: undefined });
                    }}
                    open={this.state.smsPaymentRequestDialogOpen}
                    title="SMS Request for Payment"
                    extraPlaceholders={this.state.paymentUrl}
                />
            </Row>
            </div>
        );
    }
}

PaymentForm.propTypes = {
    loadingCurrent: PropTypes.bool.isRequired,
    owedAmount: PropTypes.number,
    caseEvents: PropTypes.array,
    showAsSettingDialog: PropTypes.bool,
};

const mapStateToProps = (state, ownProps) => {
    const owner = state.user.owner;
    const {accountType, account} = getPaymentAccount(owner);

    const currentCase = state.cases.current;
    let caseType;
    if (currentCase && currentCase.intake) {
        caseType = currentCase.intake['CaseType'];
    }

    const currentConfig = state.user.config;

    const initialValues = {};
    const payment = (currentCase && currentCase.payment) || {};
    for (let i = 0; i < fields.length; i++) {
        initialValues[fields[i]] = payment[fields[i]];
    }

    return {
        initialValues,
        currentCase,
        currentConfig,
        caseType,
        loadingCurrent: state.cases.loadingCurrent,
        templates: state.cases.templates,
        loggedIn: state.user.loggedIn,
        account,
        accountType,
        tasksToggled: state.user.tasksToggled,
        employees: state.user.employees,
        tasks: filterTasks(state.user.tasks, currentCase && currentCase.id, false),
    };
};


const formikWrapper = {
    mapPropsToValues: props => props.initialValues,
    validate: validatePaymentForm,
    enableReinitialize: true,
};

export default connect(mapStateToProps)(withFormik(formikWrapper)(PaymentForm));

